import { useRef, useState, useContext } from "react";
import Form from "react-bootstrap/Form";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { Label } from "reactstrap";

//Common
import {
  TextField,
  ConfirmationDialog,
  Chip,
  DatePicker,
  Autocomplete,
} from "../common/VFX-UI";
import useComponentGeneralStyles from "../styles";
import ActionBar from "../common/ActionBar/ActionBar";
import FormBackGround from "../common/Forms/FormBackGround/FormBackGround";
import FormHeader from "../common/Forms/FormHeader/FormHeader";

//App
import {
    OrganizationEmployeeContextData,
  OrganizationEmployeeFormProps,
} from "../../types/organizationEmployee";
import { EMPTY_GUID } from "../../utility/constants/constants";
import { employeeStatus } from "./constants";
import { EmployeeContext } from "./EmployeeContainer";

const minDate = new Date("1/1/1900");
const maxDate = new Date("1/1/2100");

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  emailAddress: Yup.string()
    .email("Enter a valid email")
    .required("Email Address is required"),
  personnelNumber: Yup.string().required("Personnel Number is required"),
  stepUpEnrollmentDate: Yup.date().nullable().min(minDate, 'Date cannot be this early')
  .max(maxDate, 'Date too much in the future')
  .test('format', 'Date is invalid', date => (date?.getFullYear() ?? 0) <= 9999),
  domainLanId: Yup.string().required("Domain Lan Id is required"),
  leadershipEnrollmentDate: Yup.date().nullable().min(minDate, 'Date cannot be this early')
  .max(new Date(), 'Date cannot be in the future')
  .test('format', 'Date is invalid', date => (date?.getFullYear() ?? 0) <= 9999),
});

const EmployeeForm = (props: OrganizationEmployeeFormProps) => {
  const { status, initialVal, onClose, handleOnSubmit } = props;
  const formRef = useRef<FormikProps<any>>(null);
  const generalStyles = useComponentGeneralStyles();
  const employeeCtx = useContext<OrganizationEmployeeContextData | null>(EmployeeContext);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const { id, typeId } = initialVal;
  const title = id === EMPTY_GUID ? "New Employee" : "Edit Employee";

  const saveInput = () => {
    if (formRef.current) {
      if (formRef.current?.values?.isActive !== initialVal.isActive)
        setIsConfirmationDialogOpen(true);
      else formRef.current.handleSubmit();
    }
  };

  const toggleConfirmationDialogHandler = () => {
    setIsConfirmationDialogOpen(false);
  };

  const handleConfirmationDialog = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  return (
    <FormBackGround>
      <FormHeader title={title} onClose={onClose} />
      <Formik
        onSubmit={handleOnSubmit}
        initialValues={initialVal}
        validationSchema={validationSchema}
        innerRef={formRef}
        enableReinitialize
      >
        {({ values }) => (
          <Form noValidate>
            <div className="d-flex justify-content-end mb-3 me-3">
              <Chip
                label={
                  values.statusId &&
                  values.statusId.length > 0 &&
                  values.statusId[0] === employeeStatus.active
                    ? "Active"
                    : "Inactive"
                }
              />
            </div>

            <div className="mx-3">
              <TextField
                label="Personnel Number"
                type="text"
                name="personnelNumber"
                isRequired={true}
                disabled={id !== EMPTY_GUID}
              />
              <TextField
                label="First Name"
                type="text"
                name="firstName"
                isRequired={true}
              />
              <TextField
                label="Middle Initial"
                type="text"
                name="mi"
                isRequired={false}
              />
              <TextField
                label="Last Name"
                type="text"
                name="lastName"
                isRequired={true}
              />
            </div>

            <Label
              style={generalStyles.sectionHeader}
              className="border-bottom my-3 pb-2 w-100"
            >
              Contact Information
            </Label>
            <div className="mx-3">
              <TextField
                label="Email Address"
                type="text"
                name="emailAddress"
                isRequired={true}
              />
            </div>

            <Label
              style={generalStyles.sectionHeader}
              className="border-bottom my-3 pb-2 w-100"
            >
              Employment Information
            </Label>
            <div className="mx-3">
              <Autocomplete
                name="typeId"
                label="Type"
                isRequired={true}
                options={employeeCtx?.lookups?.type ?? []}
                defaultSelected={
                  typeId != null && typeId.length > 0
                     ? [typeId[0]]
                     : []
                  }
              />
              <TextField
                label="Work Personnel Area"
                type="text"
                name="workPersonnelArea"
                isRequired={false}
              />
              <TextField
                label="Organization Unit"
                type="text"
                name="organizationUnitName"
                isRequired={false}
              />
              <TextField
                label="Supervisor"
                type="text"
                name="supervisorName"
                isRequired={false}
              />
              <TextField
                label="Domain Lan Id"
                type="text"
                name="domainLanId"
                isRequired
              />
              <DatePicker
                type="date"
                label="Step-Up Enrollment Date"
                name="stepUpEnrollmentDate"
              />
              <DatePicker
                type="date"
                label="Leadership Enrollment Date"
                name="leadershipEnrollmentDate"
              />
            </div>

            <ConfirmationDialog
              open={isConfirmationDialogOpen}
              onClose={toggleConfirmationDialogHandler}
              onConfirm={handleConfirmationDialog}
              contentTitle={`Changing Employee Status`}
              contentText={"Are you sure you want to continue?"}
              confirmText={"Yes, Continue"}
              closeText={"No, Go Back"}
            ></ConfirmationDialog>
            <div className="mt-5">
              <ActionBar
                submitHandler={saveInput}
                cancelHandler={onClose}
                submitText="Submit"
                cancelText="Cancel"
                isLoading={status === "pending"}
              ></ActionBar>
            </div>
          </Form>
        )}
      </Formik>
    </FormBackGround>
  );
};

export default EmployeeForm;
